/**
 * The guide for creators: how to take part, how payouts are worked out, and the rules that keep
 * campaigns fair. The statements follow the V1 specification (docs/specification.md).
 */

import { ArrowRight, Scale } from "lucide-react";
import { Link } from "react-router";

import { Band, bandButtons, CallToActionBand, SectionIntro } from "../../components/marketing.js";
import { Section, styles } from "../../components/ui.js";
import { PayoutExample } from "../home/visuals.js";
import { GuideHero, GuidePoints, GuideSteps } from "./parts.js";

const steps = [
  {
    title: "Sign in with your wallet",
    text: "Your wallet is your Morcast account and where you are paid. No wallet yet? A Base Account creates one with a passkey.",
  },
  {
    title: "Link your accounts",
    text: "Link the X accounts or YouTube channels you publish with. Morcast reads your public content and organic analytics, and it can never post.",
  },
  {
    title: "Pick a campaign and read the brief",
    text: "Each campaign lists what content must include, what it must not, and how many items you may submit.",
  },
  {
    title: "Publish and submit",
    text: "Publish content that follows the brief, with its disclosure, during the campaign, then submit its link on the campaign's page. Morcast tells you at once if something is missing.",
  },
  {
    title: "Claim your payout",
    text: "The campaign settles between five and ten days after its end. Claim your payout from the escrow whenever you like. It never expires.",
  },
];

export function ForCreators() {
  return (
    <>
      <GuideHero
        eyebrow="For creators"
        title="Get paid for the reach you deliver"
        documentTitle="For creators"
        actions={
          <>
            <Link to="/campaigns" className={styles.primaryButtonLarge}>
              Find a campaign
              <ArrowRight aria-hidden className="size-4" />
            </Link>
            <Link to="/accounts" className={styles.secondaryButtonLarge}>
              Link your account
            </Link>
          </>
        }
        visual={<PayoutExample />}
      >
        No follower minimums and no applications. Every campaign's budget is already locked in the
        escrow, and you earn in proportion to the organic reach of the content you submit.
      </GuideHero>

      <Band
        tone="raised"
        innerClassName="grid items-start gap-6 py-16 sm:py-20 lg:grid-cols-[minmax(0,1fr)_26rem]"
      >
        <Section title="How to take part" className="sm:p-8">
          <GuideSteps steps={steps} />
        </Section>
        <Section title="How your payout is worked out" className="lg:sticky lg:top-24 sm:p-8">
          <GuidePoints
            points={[
              "Your total is the organic reach of the accepted content from all your accounts. That means impressions on X and views on YouTube.",
              "A total below the campaign's threshold scores 0. Above it, your score is your total.",
              "Until all scores together reach the target, each unit of your score earns 80% of the budget divided by the target, whatever others do.",
              "Above the target, creators share 80% of the budget in proportion to their scores.",
            ]}
          />
        </Section>
      </Band>

      <Band innerClassName="py-16 sm:py-24">
        <SectionIntro eyebrow="Fair for everyone" title="Simple rules, applied the same way to all">
          Every creator's content is checked the same way, and nobody is excluded on suspicion
          alone.
        </SectionIntro>
        <Section title="Rules that keep campaigns fair" icon={Scale} className="mt-12 sm:p-8">
          <div className="grid gap-x-12 md:grid-cols-2">
            <GuidePoints
              points={[
                "Only organic reach counts, so never promote your content with ads.",
                "Show the brief's disclosure, and keep your content public until the campaign is settled.",
                "Edit only as the brief allows. Other edits after submission make an item fail.",
              ]}
            />
            <div className="mt-3.5 md:mt-0">
              <GuidePoints
                points={[
                  "In each campaign, take part with up to 5 of your accounts. Each may submit as many items as the brief allows. An account belongs to one wallet at a time.",
                  "Results far above your account's usual reach go to a person before they count. They are never excluded on suspicion alone.",
                  "You can raise an issue about a result during its review, before the campaign settles.",
                ]}
              />
            </div>
          </div>
        </Section>
      </Band>

      <CallToActionBand
        title="Find a campaign and start earning"
        actions={
          <>
            <Link to="/campaigns" className={bandButtons.primary}>
              Find a campaign
              <ArrowRight aria-hidden className="size-4" />
            </Link>
            <Link to="/accounts" className={bandButtons.secondary}>
              Link your account
            </Link>
          </>
        }
      >
        Link the account you publish with once, then take part in any campaign that fits.
      </CallToActionBand>
    </>
  );
}
